import { twMerge } from '@shared/themes';
import { FRONTIFY_URL } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import type { LotteryName } from '../../../lottery-purchases/types';

type LotteryLogoProps = {
  lotteryName: LotteryName;
  className?: string;
};

/**
 * LotteryLogo component displays the appropriate logo based on the lottery identifier.
 *
 * The logo image is resolved from the lottery results translations and served from Frontify CDN.
 *
 * @param {LotteryLogoProps} props - The props for the LotteryLogo component.
 * @param {string} props.lotteryName - The identifier of the lottery.
 * @param {string} [props.className] - Additional CSS classes to apply.
 * @returns {JSX.Element | null} The rendered lottery logo component or null for unsupported lotteries.
 */
function LotteryLogo({ lotteryName, className }: LotteryLogoProps) {
  const t = useTranslations(`lotteries.${lotteryName}`);

  if (!t.has('results.logo')) return null;

  const logoSrc = `${FRONTIFY_URL}${t('results.logo')}.png`;

  return (
    <div className={twMerge('mx-auto w-full max-w-160', className)}>
      <div className="relative h-47 w-full">
        <Image src={logoSrc} alt={`${t('name')} logo`} fill sizes="(max-width: 640px) 100vw, 640px" className="object-cover object-center" />
      </div>
    </div>
  );
}

export { LotteryLogo };
